import { Box, Image } from '@chakra-ui/react';
import { IconCheck } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import 'moment/locale/it';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useAccounts } from '../../../hooks/Accounts/useAccounts';
import DropzoneFormikInput from '../../utilities/Dropzone';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { useGeneral } from '../../../hooks/General/useGeneral';
import { UIModal, UIModalBody, UIModalButtons, UiModalInternal } from '../../utilities/Modal';
import { UIPageHeader } from '../../utilities/Headers';
import { UIFormControlBox } from '../../utilities/Form';
import { UICheckbox, UIInput, UITextarea } from '../../utilities/Input';
import UIInputAutocomplete, { UIInputSelect } from '../../utilities/InputSelect';
import { UIButton } from '../../utilities/Button';

const ModalUserAdmin = ({ modalOpen, handleModalClose, user }) => {
  const [uuid, setUuid] = useState(uuidv4());
  const [userData, setUserData] = useState(null);
  const [countries, setCountries] = useState([]);

  const genders = [
    { value: 'M', label: 'Uomo' },
    { value: 'F', label: 'Donna' },
    { value: 'Other', label: 'Altro' }
  ];

  const { onUpdateUserAdmin, onGetUserAdmin } = useAccounts();
  const { onListCountries } = useGeneral();

  const getUserData = async () => {
    onGetUserAdmin({
      user_uuid: user?.uuid
    }).then((res) => {
      setUserData(res);
    });
  };

  useEffect(() => {
    if (!modalOpen) return;
    setUuid(uuidv4());
    onListCountries().then((res) => {
      setCountries(res);
    });

    if (!user) return;
    getUserData();
  }, [user, modalOpen]);

  const handleSubmit = async (values) => {
    const {
      name,
      surname,
      phone,
      dial_code_country,
      email,
      profile_pic,
      age,
      sex,
      pregnant,
      permanent_makeup,
      anamnesi,
      allergies,
      other_allergies,
      on_boarding_complete,
      active
    } = values;

    onUpdateUserAdmin({
      user_id: userData.id,
      user_data: {
        name,
        surname,
        phone,
        dial_code_country,
        email,
        profile_pic,
        age,
        sex,
        pregnant,
        permanent_makeup,
        anamnesi,
        allergies,
        other_allergies,
        on_boarding_complete,
        active
      }
    }).then((res) => {
      toast(res?.response, {
        style: {
          fontSize: '14px',
          backgroundColor: res?.responseStatus ? '#00e676' : '#FF646C',
          color: '#ffffff'
        }
      });
      if (res?.responseStatus) {
        handleModalCloseInternal();
      }
    });
  };

  const handleModalCloseInternal = () => {
    setUserData(null);
    handleModalClose();
  };

  return (
    <UIModal open={modalOpen} onClose={handleModalCloseInternal} allowFullScreen={true} className={'modal'}>
      <UiModalInternal
        onClose={handleModalCloseInternal}
        isLoading={userData === null}
        containerSx={{
          bgcolor: '#FFF'
        }}
      >
        <Formik
          initialValues={{
            name: userData?.name ?? '',
            surname: userData?.surname ?? '',
            phone: userData?.phone ?? '',
            dial_code_country: userData?.dial_code_country ?? '',
            email: userData?.email ?? '',
            profile_pic: userData?.profile_pic ?? '',
            age: userData?.age ?? '',
            sex: userData?.sex ?? '',
            pregnant: userData?.pregnant ?? false,
            permanent_makeup: userData?.permanent_makeup ?? false,
            anamnesi: userData?.anamnesi ?? '',
            allergies: userData?.allergies ?? '',
            other_allergies: userData?.other_allergies ?? '',
            on_boarding_complete: userData?.on_boarding_complete ?? false,
            active: userData?.active ?? false
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Campo obbligatorio'),
            surname: Yup.string().required('Campo obbligatorio'),
            email: Yup.string().required('La mail è obbligatoria'),
            dial_code_country: Yup.string().required('Campo obbligatorio'),
            phone: Yup.string().required('Campo obbligatorio'),
            profile_pic: Yup.string().nullable(),
            age: Yup.number().required('Campo obbligatorio'),
            sex: Yup.string().required('Campo obbligatorio'),
            pregnant: Yup.boolean().nullable(),
            permanent_makeup: Yup.boolean().nullable(),
            anamnesi: Yup.string().nullable(),
            allergies: Yup.string().nullable(),
            other_allergies: Yup.string().nullable(),
            on_boarding_complete: Yup.boolean().nullable(),
            active: Yup.boolean().nullable()
          })}
          onSubmit={(values, { setErrors }) => handleSubmit(values, setErrors)}
          enableReinitialize={true}
        >
          {({ errors, handleBlur, handleSubmit, touched, values, setFieldValue, submitForm }) => (
            <form onSubmit={handleSubmit} id={'appointment-form'}>
              <UIModalBody>
                <UIPageHeader
                  title={user ? 'Modifica utente' : 'Aggiungi utente'}
                  sx={{
                    textAlign: 'center'
                  }}
                />
                <UIFormControlBox
                  label={'Immagine'}
                  error={Boolean(touched.profile_pic && errors.profile_pic)}
                  error_message={errors?.profile_pic}
                >
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={'0 10px'}>
                    <Image src={values.profile_pic} w={'100px'} h={'100px'} objectFit={'cover'} />
                    <DropzoneFormikInput
                      variant={'profile_pic'}
                      setFieldValue={setFieldValue}
                      field={'profile_pic'}
                      folder={'profile_pic'}
                      label={'Carica'}
                      extra_data={{
                        account_uuid: user?.uuid ?? uuid
                      }}
                    />
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox label={'Nome'} error={Boolean(touched.name && errors.name)}>
                  <UIInput value={values.name} onChange={(event) => setFieldValue('name', event.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Cognome'} error={Boolean(touched.surname && errors.surname)}>
                  <UIInput value={values.surname} onChange={(event) => setFieldValue('surname', event.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox
                  error_message={errors?.dial_code_country}
                  label={'Prefisso'}
                  error={Boolean(touched.dial_code_country && errors.dial_code_country)}
                >
                  <UIInputAutocomplete
                    value={Number(values?.dial_code_country)}
                    setValue={(value) => {
                      if (value?.id) {
                        setFieldValue('dial_code_country', value?.id);
                      }
                    }}
                    options={countries?.map((country) => {
                      return {
                        id: Number(country?.id),
                        label: `${country?.dial_code} - ${country?.name}`
                      };
                    })}
                    needTimeoutText={false}
                    handleBlur={handleBlur}
                  />
                </UIFormControlBox>

                <UIFormControlBox label={'Cellulare'} error={Boolean(touched.phone && errors.phone)}>
                  <UIInput value={values.phone} onChange={(event) => setFieldValue('phone', event.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Email'} error={Boolean(touched.email && errors.email)}>
                  <UIInput value={values.email} onChange={(event) => setFieldValue('email', event.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Età'} error={Boolean(touched.age && errors.age)}>
                  <UIInput value={values.age} onChange={(event) => setFieldValue('age', event.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox error_message={errors?.sex} label={'Genere'} error={Boolean(touched.sex && errors.sex)}>
                  <UIInputSelect
                    fullWidth={true}
                    value={values?.sex}
                    onChange={(e) => setFieldValue('sex', e.target.value)}
                    options={genders}
                  />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.pregnant && errors.pregnant)}>
                  <UICheckbox
                    name={'pregnant'}
                    label={'Incinta'}
                    checked={values?.pregnant}
                    onChange={(event) => setFieldValue('pregnant', event.target.checked)}
                  />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.permanent_makeup && errors.permanent_makeup)}>
                  <UICheckbox
                    name={'permanent_makeup'}
                    label={'Makup permanenti'}
                    checked={values?.permanent_makeup}
                    onChange={(event) => setFieldValue('permanent_makeup', event.target.checked)}
                  />
                </UIFormControlBox>

                <UIFormControlBox label={'Anamnesi'} error={Boolean(touched.anamnesi && errors.anamnesi)}>
                  <UITextarea
                    value={values?.anamnesi}
                    onChange={(event) => setFieldValue('anamnesi', event.target.value)}
                    expandable={false}
                  />
                </UIFormControlBox>

                <UIFormControlBox
                  label={'Allergie a principi attivi, farmaci o molecole'}
                  error={Boolean(touched.allergies && errors.allergies)}
                >
                  <UITextarea
                    value={values?.allergies}
                    onChange={(event) => setFieldValue('allergies', event.target.value)}
                    expandable={false}
                  />
                </UIFormControlBox>

                <UIFormControlBox label={'Altre allergie generali'} error={Boolean(touched.other_allergies && errors.other_allergies)}>
                  <UITextarea
                    value={values?.other_allergies}
                    onChange={(event) => setFieldValue('other_allergies', event.target.value)}
                    expandable={false}
                  />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.on_boarding_complete && errors.on_boarding_complete)}>
                  <UICheckbox
                    name={'on_boarding_complete'}
                    label={'Onboarding completato'}
                    checked={values?.on_boarding_complete}
                    onChange={(event) => setFieldValue('on_boarding_complete', event.target.checked)}
                  />
                </UIFormControlBox>
                <UIFormControlBox error={Boolean(touched.active && errors.active)}>
                  <UICheckbox
                    name={'active'}
                    label={'Attivo'}
                    checked={values?.active}
                    onChange={(event) => setFieldValue('active', event.target.checked)}
                  />
                </UIFormControlBox>
              </UIModalBody>
              <UIModalButtons>
                <UIButton type={'submit'} onClick={() => submitForm()} icon={<IconCheck stroke={'1.64'} size={'26px'} />} />
              </UIModalButtons>
            </form>
          )}
        </Formik>
      </UiModalInternal>
    </UIModal>
  );
};
export default ModalUserAdmin;
